import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Select, Status, Tag, Thumb } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useCan } from '../app/me.tsx';

/**
 * Screen 12: what a take was shot on, what it was turned into, and what
 * swapping a sequence to the final splat would cost.
 *
 * The cost is the part that matters. Swapping is one click that makes every
 * render of that sequence stale, which can be fifty renders somebody then has
 * to re-run — so the number is shown before the confirmation, worked out by the
 * same rule that will do it.
 */

interface CaptureScreen {
  take: { _id: string; scene?: string; shot?: string; take?: string };
  physical_cameras: {
    cam_id: string;
    missing: number;
    says: string;
    where: { uri: string | null; label: string }[];
    thumbnail: string | null;
  }[];
  manifest_hash: string | null;
  splat_plan: {
    variant: string;
    settings: Record<string, unknown>;
    version: number | null;
    status: string | null;
    owner: string | null;
    machine: string | null;
    thumbnail: string | null;
    says: string;
    can_rerun: boolean;
  }[];
  sequences: {
    sequence_id: string;
    name: string;
    on: string;
    final_ready: boolean;
    swap_effect: { renders_made_stale: number; passes: number; blocked_passes: number; says: string; refused: string | null } | null;
  }[];
}

export function Capture({ projectId, takeId }: { projectId: string; takeId: string | null }) {
  const can = useCan();
  const [chosen, setChosen] = useState<string>(takeId ?? '');
  const [swapping, setSwapping] = useState<CaptureScreen['sequences'][number] | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { state: takes } = useLoad<{ takes: { _id: string; scene?: string; shot?: string; take?: string }[] }>(
    () => api.get(`/api/projects/${encodeURIComponent(projectId)}/takes`),
    [projectId]
  );
  const takeToShow = chosen || takes.data?.takes[0]?._id || '';

  // A capture belongs to a take, so with no takes there is nothing to ask
  // about. This used to reject, and a rejected load is drawn as "the server
  // did not answer" — which was a plain untruth: the server answered, the
  // project is simply empty.
  const { state, reload } = useLoad<CaptureScreen | null>(
    () => (takeToShow ? api.get<CaptureScreen>(`/api/takes/${takeToShow}/capture`) : Promise.resolve(null)),
    [takeToShow]
  );
  const screen = state.data ?? null;
  /** The take list came back and there was nothing in it. */
  const noTakes = takes.status === 'ready' && (takes.data?.takes.length ?? 0) === 0;

  const swap = async (): Promise<void> => {
    if (!swapping) return;
    try {
      await api.post(`/api/sequences/${swapping.sequence_id}/swap-to-final`, {});
      setSwapping(null);
      setToast('Swapped to the final splat. The renders behind it are now stale.');
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That could not be swapped.');
    }
  };

  const name = screen
    ? [screen.take.scene, screen.take.shot, screen.take.take].filter(Boolean).join(' · ')
    : 'Capture';

  return (
    <Page
      eyebrow="MAESTRO · CAPTURE AND SPLATS"
      title={name || 'Capture'}
      description="The physical cameras, what the capture was turned into, and the sequences built on it."
    >
      {(takes.data?.takes.length ?? 0) > 1 && (
        <Card>
          <Field label="Which take">
            <Select
              value={takeToShow}
              onChange={setChosen}
              options={(takes.data?.takes ?? []).map((t) => ({
                value: t._id,
                label: [t.scene, t.shot, t.take].filter(Boolean).join(' · ') || t._id
              }))}
            />
          </Field>
        </Card>
      )}

      {noTakes && (
        <Empty
          title="This project has no takes yet"
          says="A capture belongs to a take — one attempt at one shot. Takes come from Conductor, and the capture, its physical cameras and the splat built from it appear here once there is one."
        />
      )}

      {!noTakes && state.status === 'loading' && !screen && (
        <Loading says="Loading the capture…" rows={5} />
      )}
      {!noTakes && state.status === 'failed' && !screen && (
        <CouldNotLoad what="this capture" error={state.error} onRetry={reload} />
      )}
      {screen && screen.physical_cameras.length === 0 && (
        <Empty title="No capture recorded" says="Add one, with its physical cameras and a manifest hash." />
      )}

      {screen && screen.physical_cameras.length > 0 && (
        <Card title="Physical cameras" aside={screen.manifest_hash ? `manifest ${screen.manifest_hash}` : undefined}>
          {screen.physical_cameras.map((cam) => (
            <div
              key={cam.cam_id}
              style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'center' }}
            >
              <Thumb url={cam.thumbnail} alt={`camera ${cam.cam_id}`} size={88} />
              <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: space.tight }}>
                  <span style={text.bodyStrong}>Camera {cam.cam_id}</span>
                  {cam.missing > 0 && <Status value="bad" detail={`${cam.missing} missing`} />}
                </span>
                <span style={{ ...text.small, color: cam.missing > 0 ? colour.staleInk : colour.inkSoft }}>
                  {cam.says}
                </span>
                {cam.where.map((where) => (
                  <Tag key={where.label} tone="mono">
                    {where.label}
                  </Tag>
                ))}
              </div>
            </div>
          ))}
        </Card>
      )}

      {screen && (
        <Card title="The fan-out" aside="What this capture is turned into.">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
              gap: space.control
            }}
          >
            {screen.splat_plan.map((output) => (
              <div
                key={output.variant}
                style={{
                  display: 'flex',
                  gap: space.tight,
                  padding: space.control,
                  borderRadius: 8,
                  border: `1px solid ${colour.edge}`,
                  background: colour.ground
                }}
              >
                <Thumb url={output.thumbnail} alt={`${output.variant} splat`} size={80} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span style={text.smallStrong}>{output.variant} splat</span>
                  <span style={{ ...text.note, color: colour.inkSoft }}>{output.says}</span>
                  {output.owner && (
                    <span style={{ ...text.note, color: colour.inkMuted }}>
                      {output.owner}
                      {output.machine ? ` · ${output.machine}` : ''}
                    </span>
                  )}
                  {Object.entries(output.settings).map(([key, value]) => (
                    <Tag key={key}>{`${key}: ${String(value)}`}</Tag>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {screen && screen.sequences.length > 0 && (
        <Card title="Sequences" aside="Which splat each one is on.">
          {screen.sequences.map((sequence) => (
            <div
              key={sequence.sequence_id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: space.control,
                alignItems: 'center',
                paddingTop: space.tight,
                borderTop: `1px solid ${colour.edgeSoft}`
              }}
            >
              <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: space.tight }}>
                  <span style={text.bodyStrong}>{sequence.name}</span>
                  <Tag>on {sequence.on}</Tag>
                  {sequence.final_ready && <Status value="stale" detail="a good final splat is waiting" />}
                </span>
                {sequence.swap_effect && (
                  <span style={{ ...text.note, color: colour.inkSoft }}>{sequence.swap_effect.says}</span>
                )}
              </div>
              {can.launch && sequence.swap_effect && !sequence.swap_effect.refused && (
                <Button onClick={() => setSwapping(sequence)}>Swap to final</Button>
              )}
            </div>
          ))}
        </Card>
      )}

      {swapping?.swap_effect && (
        <Dialog
          title={`Swap ${swapping.name} to the final splat`}
          says={`${swapping.swap_effect.says} This makes a new version of the sequence, and swapping back would not undo it — that would be another new version.`}
          confirm="Swap it"
          tone="danger"
          onConfirm={() => void swap()}
          onClose={() => setSwapping(null)}
        >
          <span style={{ ...text.small, color: colour.inkSoft }}>
            {swapping.swap_effect.passes} passes use this sequence.
            {swapping.swap_effect.blocked_passes > 0 &&
              ` ${swapping.swap_effect.blocked_passes} are blocked on a VC and are not in the number.`}
          </span>
        </Dialog>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
